import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  type TypeOfObjectValues,
} from "../util/index.ts";
import { AssetType, AssetTypeSchema } from "./AssetSchemas.ts";
import { AttributeDataType } from "./AttributeSchemas.ts";
import { NotificationChannelSchema } from "./NotificationSchema.ts";
import {
  WORKFLOW_VARNAME_REGEX,
  WORKFLOW_VAR_NAME_FORMAT,
  WorkflowConfigSchema,
  WorkflowTriggerTimePeriodSchema,
  WorkflowVisualizationMetadataSchema,
} from "./WorkflowConfigSchemas.ts";
import {
  WorkflowTaskNotificationEventType,
  WorkflowTaskNotificationRecipientType,
} from "./WorkflowTaskSchemas.ts";

const RESERVED_KEY = "$VALIDERE";
export const MIN_VAR_NAME_LENGTH = 3;
export const MAX_VAR_NAME_LENGTH = 64;
export const MIN_STATIC_VALUE_LENGTH = 1;
export const MAX_STATIC_VALUE_LENGTH = 1024;
export const MIN_PATH_LENGTH = 1;
export const MAX_PATH_LENGTH = 1024;
export const WORKFLOW_VAR_EQUATION_VARIABLE_FORMAT =
  "equation variable name must be a combination of letters and numbers and start with a letter.";
export const WORKFLOW_STATIC_VALUE_FORMAT =
  "static value ​​cannot start with $";
export const WORKFLOW_VAR_TIME_VALUE_UNIT_FORMAT =
  "datetime-like values must be a valid datetime-like value. e.g. 1 year, 2 months, 3 hour";
export const WORKFLOW_PATH_FORMAT =
  "path must be a JSON path to specify the JSON fields starting with $";
export const WORKFLOW_VAR_NAME_RESERVED_NAMESPACE = `variable name must not start with ${RESERVED_KEY}, reserved namespace`;
export const WORKFLOW_VAR_EQUATION_VARIABLE_REGEX = /^[a-zA-Z][a-zA-Z0-9]*$/;
const isValidName = (name: string) => !name.startsWith(RESERVED_KEY);

export const VariableNameSchema = z
  .string()
  .min(MIN_VAR_NAME_LENGTH)
  .max(MAX_VAR_NAME_LENGTH)
  .regex(WORKFLOW_VARNAME_REGEX, WORKFLOW_VAR_NAME_FORMAT)
  .refine(isValidName, WORKFLOW_VAR_NAME_RESERVED_NAMESPACE);
export const StaticValueSchema = z
  .string()
  .min(MIN_STATIC_VALUE_LENGTH)
  .max(MAX_STATIC_VALUE_LENGTH)
  .regex(/^(?!\$).*$/, WORKFLOW_STATIC_VALUE_FORMAT);
export const VariablePathSchema = z
  .string()
  .min(MIN_PATH_LENGTH)
  .max(MAX_PATH_LENGTH)
  .regex(/^\$.*$/, WORKFLOW_PATH_FORMAT);
export const EquationVariableNameSchema = z
  .string()
  .min(MIN_VAR_NAME_LENGTH)
  .max(MAX_VAR_NAME_LENGTH)
  .regex(
    WORKFLOW_VAR_EQUATION_VARIABLE_REGEX,
    WORKFLOW_VAR_EQUATION_VARIABLE_FORMAT
  )
  .refine(isValidName, WORKFLOW_VAR_NAME_RESERVED_NAMESPACE);
export const TimeValueWithUnitSchema = z
  .string()
  .min(MIN_STATIC_VALUE_LENGTH)
  .max(MAX_STATIC_VALUE_LENGTH)
  .regex(
    /^\d+\s(day|days|week|weeks|month|months|year|years|second|seconds|minute|minutes|hour|hours)$/,
    WORKFLOW_VAR_TIME_VALUE_UNIT_FORMAT
  );

export const WorkflowTriggerType = {
  SCHEDULED: "scheduled",
  SYSTEM: "system",
} as const;

export type WorkflowTriggerTypeType = TypeOfObjectValues<
  typeof WorkflowTriggerType
>;

export const WorkflowTriggerDisplayType = {
  DUMMY: "dummy",
} as const;

export const WorkflowTriggerTypeSchema = z.nativeEnum(WorkflowTriggerType);

export const WorkflowTriggerEvent = {
  FORM_SUBMISSION_CREATE: "form_submission_create",
  ASSET_CREATE: "asset_create",
  ASSET_UPDATE: "asset_update",
  WORKFLOW_COMPLETE: "workflow_complete",
} as const;

export const WorkflowTriggerEventSchema = z.nativeEnum(WorkflowTriggerEvent);

export type WorkflowTriggerEventType = TypeOfObjectValues<
  typeof WorkflowTriggerEvent
>;

const WorkflowTriggerRelativeDateSchema = z.object({
  time_period: WorkflowTriggerTimePeriodSchema,
  duration: z.number().positive(),
});

export const WorkflowTriggerDueDateSchema =
  WorkflowTriggerRelativeDateSchema.extend({
    start_variable: z.string().optional(),
  });

export type WorkflowTriggerDueDate = z.infer<
  typeof WorkflowTriggerDueDateSchema
>;

export const WorkflowBaseTriggerSchema = z.object({
  type: WorkflowTriggerTypeSchema,
  due_date: WorkflowTriggerDueDateSchema.optional(),
  notification_date: WorkflowTriggerRelativeDateSchema.optional(),
  output: z.record(z.string(), z.string()).optional(),
  visualization_metadata: WorkflowVisualizationMetadataSchema.optional(),
});

export type WorkflowBaseTrigger = z.infer<typeof WorkflowBaseTriggerSchema>;

// Used in FE workflow configuration to create a dummy trigger that can be used to configure an actual trigger compatible with the API.
export const WorkflowTriggerDummySchema = WorkflowBaseTriggerSchema.extend({
  type: z.literal(WorkflowTriggerDisplayType.DUMMY),
  event: z.literal(WorkflowTriggerDisplayType.DUMMY),
});
export type WorkflowTriggerDummy = z.infer<typeof WorkflowTriggerDummySchema>;

export const WorkflowTriggerScheduledSchema = WorkflowBaseTriggerSchema.extend({
  type: z.literal(WorkflowTriggerType.SCHEDULED),
  cron: z.string().optional(),
});

export type WorkflowTriggerScheduled = z.infer<
  typeof WorkflowTriggerScheduledSchema
>;

const FormAnswerFilterSchema = z.object({
  id: z.string().uuid().optional(),
  submission_id: z.string().uuid().optional(),
  section_id: z.string().optional(),
  question_index: z.number().int().optional(),
  question_id: z.string(),
  "answer.value": z.string().or(z.number()).or(z.any()),
});

export type WorkflowTriggerFormCreateAnswerFilterType = z.infer<
  typeof FormAnswerFilterSchema
>;

export const WorkflowTriggerFormCreateSchema = WorkflowBaseTriggerSchema.extend(
  {
    type: z.literal(WorkflowTriggerType.SYSTEM),
    event: z.literal(WorkflowTriggerEvent.FORM_SUBMISSION_CREATE),
    form_schema_id: z.string().uuid(),
    answer_filter: z
      .union([FormAnswerFilterSchema, z.array(FormAnswerFilterSchema)])
      .optional(),
  }
);

export type WorkflowTriggerFormCreate = z.infer<
  typeof WorkflowTriggerFormCreateSchema
>;

export const WorkflowTriggerAssetCreateSchema =
  WorkflowBaseTriggerSchema.extend({
    type: z.literal(WorkflowTriggerType.SYSTEM),
    event: z.literal(WorkflowTriggerEvent.ASSET_CREATE),
    asset_type: AssetTypeSchema,
    asset_filter: z.record(z.string(), z.any()),
  });
export type WorkflowTriggerAssetCreate = z.infer<
  typeof WorkflowTriggerAssetCreateSchema
>;

export const WorkflowTriggerAssetUpdateSchema =
  WorkflowBaseTriggerSchema.extend({
    type: z.literal(WorkflowTriggerType.SYSTEM),
    event: z.literal(WorkflowTriggerEvent.ASSET_UPDATE),
    asset_type: AssetTypeSchema,
    asset_filter: z.record(z.string(), z.any()),
    attributes: z.array(z.string()).optional().default([]),
  });
export type WorkflowTriggerAssetUpdate = z.infer<
  typeof WorkflowTriggerAssetUpdateSchema
>;

export const WorkflowTriggerWorkflowCompleteSchema =
  WorkflowBaseTriggerSchema.extend({
    type: z.literal(WorkflowTriggerType.SYSTEM),
    event: z.literal(WorkflowTriggerEvent.WORKFLOW_COMPLETE),
    workflow_template_id: z.string().uuid(),
  });

export type WorkflowTriggerWorkflowComplete = z.infer<
  typeof WorkflowTriggerWorkflowCompleteSchema
>;

export const WorkflowTriggerSchema = z.union([
  WorkflowTriggerDummySchema,
  WorkflowTriggerScheduledSchema,
  WorkflowTriggerFormCreateSchema,
  WorkflowTriggerAssetCreateSchema,
  WorkflowTriggerAssetUpdateSchema,
  WorkflowTriggerWorkflowCompleteSchema,
]);

export type WorkflowTriggerType = z.infer<typeof WorkflowTriggerSchema>;

export type WorkflowConfigurationType = z.infer<typeof WorkflowConfigSchema>;

export const WorkflowTemplateStatus = {
  ACTIVE: "active",
  DRAFT: "draft",
  ARCHIVE: "archive",
} as const;

export const WorkflowNotificationEventType = {
  CREATED: "created",
  STATUS_CHANGED: "status_changed",
  DELETED: "deleted",
} as const;

export const WorkflowNotificationRecipientType = {
  OWNER: "owner",
  ALL_ASSIGNEES: "all_assignees",
  USERS: "users",
} as const;

export type WorkflowTemplateStatusType = TypeOfObjectValues<
  typeof WorkflowTemplateStatus
>;

const OutputDynamicVariableDateAndTimeBaseSchema = z.object({
  data_type: z.enum([AttributeDataType.DATE]),
  format: z.enum(["YYYYMM", "YYYY", "YYYY-MM-DD"]),
});

export const OutputDynamicVariableWithValueSchema =
  OutputDynamicVariableDateAndTimeBaseSchema.extend({
    value: z.union([
      z.date(),
      z.string().datetime(),
      z.string().date(),
      VariablePathSchema,
    ]),
  });

const OutputDynamicVariableWithEquationAndSubstitutionsSchema =
  OutputDynamicVariableDateAndTimeBaseSchema.extend({
    equation: z.string(),
    substitutions: z.record(
      EquationVariableNameSchema,
      z.union([VariablePathSchema, StaticValueSchema, TimeValueWithUnitSchema])
    ),
  });

const OutputDynamicVariableDateAndTimeSchema = z.union([
  OutputDynamicVariableWithValueSchema,
  OutputDynamicVariableWithEquationAndSubstitutionsSchema,
]);

const OutputDynamicVariableSchema = OutputDynamicVariableDateAndTimeSchema;

export const WorkflowTemplateOutputSchema = z.record(
  VariableNameSchema,
  z.union([VariablePathSchema, StaticValueSchema, OutputDynamicVariableSchema])
);

export type WorkflowTemplateOutput = z.input<
  typeof WorkflowTemplateOutputSchema
>;

const workflowTemplateInitialVariableBase = z.object({
  display_name: z.string().min(3).max(64),
  description: z.string().max(256),
  is_required: z.boolean(),
  order: z.number().int(),
});

const workflowTemplateInitialVariableDate =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.DATE),
    format: z.enum(["YYYYMM", "YYYY", "YYYY-MM-DD", "ISO"]),
  });

const workflowTemplateInitialVariableDateTime =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.DATE_TIME),
    format: z.literal("ISO"),
  });

const workflowTemplateInitialVariableInteger =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.INTEGER),
  });

const workflowTemplateInitialVariableNumber =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.NUMBER),
  });

const workflowTemplateInitialVariableString =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.STRING),
  });

const workflowTemplateInitialVariableBoolean =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.BOOLEAN),
  });

const workflowTemplateInitialVariablePickList =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.PICK_LIST),
    pick_list_values: z.array(z.string()),
  });

const workflowTemplateInitialVariableMultiPickList =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.MULTI_PICK_LIST),
    pick_list_values: z.array(z.string()),
  });

const workflowTemplateInitialVariableLookup =
  workflowTemplateInitialVariableBase.extend({
    data_type: z.literal(AttributeDataType.LOOKUP),
    lookup_entity_type: z.nativeEnum(AssetType),
    filter: z.record(z.string(), z.unknown()).optional(),
  });

export const WorkflowTemplateInitialVariableSchema = z.discriminatedUnion(
  "data_type",
  [
    workflowTemplateInitialVariableDate,
    workflowTemplateInitialVariableDateTime,
    workflowTemplateInitialVariableInteger,
    workflowTemplateInitialVariableNumber,
    workflowTemplateInitialVariableString,
    workflowTemplateInitialVariableBoolean,
    workflowTemplateInitialVariablePickList,
    workflowTemplateInitialVariableMultiPickList,
    workflowTemplateInitialVariableLookup,
  ]
);

const WorkflowTemplateTypeSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    status: z.enum([
      WorkflowTemplateStatus.ACTIVE,
      WorkflowTemplateStatus.DRAFT,
      WorkflowTemplateStatus.ARCHIVE,
    ]),
    version: z.number(),
    category_id: z.string(),
    config: WorkflowConfigSchema,
    triggers: z.array(WorkflowTriggerSchema),
    company_id: z.string(),
    workflow_category: z.object({
      id: z.string(),
      description: z.string(),
      name: z.string(),
    }),
    asset_type: AssetTypeSchema,
    asset_filter: z.any().nullable(),
    workflow_owner_id: z.string(),
    workflow_notifications: z.object({
      events: z.array(
        z.object({
          type: z.enum([
            WorkflowNotificationEventType.CREATED,
            WorkflowNotificationEventType.STATUS_CHANGED,
            WorkflowNotificationEventType.DELETED,
          ]),
        })
      ),
      recipients: z.array(
        z.union([
          z.object({
            type: z.enum([
              WorkflowNotificationRecipientType.ALL_ASSIGNEES,
              WorkflowNotificationRecipientType.OWNER,
            ]),
          }),
          z.object({
            type: z.enum([WorkflowNotificationRecipientType.USERS]),
            users: z.array(z.string()),
          }),
        ])
      ),
      channels: z.array(NotificationChannelSchema),
    }),

    workflow_task_notifications: z.object({
      events: z.array(
        z.object({
          type: z.lazy(() =>
            z.enum([
              WorkflowTaskNotificationEventType.CREATED,
              WorkflowTaskNotificationEventType.STATUS_CHANGED,
              WorkflowTaskNotificationEventType.ASSIGNEE_CHANGED,
            ])
          ),
        })
      ),
      recipients: z.array(
        z.union([
          z.object({
            type: z.lazy(() =>
              z.enum([
                WorkflowTaskNotificationRecipientType.ASSIGNEE,
                WorkflowTaskNotificationRecipientType.ASSIGNEE_USER,
                WorkflowTaskNotificationRecipientType.ASSIGNEE_GROUP,
                WorkflowTaskNotificationRecipientType.WORKFLOW_OWNER,
              ])
            ),
          }),
          z.object({
            type: z.enum([WorkflowNotificationRecipientType.USERS]),
            users: z.array(z.string()),
          }),
        ])
      ),
      channels: z.array(NotificationChannelSchema),
    }),

    initial_variables: z.record(
      z.string(),
      WorkflowTemplateInitialVariableSchema
    ),

    output: WorkflowTemplateOutputSchema,
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("The domain model schema for a Workflow Template");

export type WorkflowTemplateType = z.infer<typeof WorkflowTemplateTypeSchema>;
export type WorkflowTriggerFormCreateType = z.infer<
  typeof WorkflowTriggerFormCreateSchema
>;
export type WorkflowTriggerWorkflowCompleteType = z.infer<
  typeof WorkflowTriggerWorkflowCompleteSchema
>;
export type WorkflowTriggerScheduledSchemaType = z.infer<
  typeof WorkflowTriggerScheduledSchema
>;

// Helper functions to determine the type of workflow trigger:
export const isTriggerFormSubmissionCreate = (
  trigger: WorkflowTemplateType["triggers"][0]
): trigger is WorkflowTriggerFormCreateType =>
  "event" in trigger && trigger.event === "form_submission_create";

export const isTriggerAssetCreate = (
  trigger: WorkflowTemplateType["triggers"][0]
): trigger is WorkflowTriggerAssetCreate =>
  trigger.type === "system" && trigger.event === "asset_create";

export const isTriggerAssetUpdate = (
  trigger: WorkflowTemplateType["triggers"][0]
): trigger is WorkflowTriggerAssetUpdate =>
  trigger.type === "system" && trigger.event === "asset_update";

export const isTriggerOtherWorkflowCompletion = (
  trigger: WorkflowTemplateType["triggers"][0]
): trigger is WorkflowTriggerWorkflowComplete =>
  trigger.type === "system" && trigger.event === "workflow_complete";

export const isTriggerCron = (
  trigger: WorkflowTemplateType["triggers"][0]
): trigger is WorkflowTriggerScheduledSchemaType =>
  trigger.type === "scheduled";
